import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";

import { EMAIL_WARNING_MODAL_CLASS_NAME } from "@app/util/email/const";
import useMapCanvasProjection from "@app/util/hook/map/useMapCanvasProjection";
import { useRawSizeObserver } from "@app/util/hook/useSizeObserver";

import useAnnotationCoordinates from "../utils/useAnnotationCoordinates";

const X_GAP = 40;
const Y_GAP = 0;

interface OverlayProps {
  children: ReactNode;
  coordinates?: { left: number; top: number };
  map?: google.maps.Map;
  onClickOutside?: () => void;
  position?: google.maps.LatLng | google.maps.LatLngLiteral;
  usedInModal?: boolean;
  zIndex?: number;
}

const Overlay: FC<OverlayProps> = ({
  children,
  coordinates,
  map,
  onClickOutside,
  position,
  usedInModal,
  zIndex,
}: OverlayProps) => {
  const projection = useMapCanvasProjection(map!);
  const { left, top } = useAnnotationCoordinates(map, projection, position, coordinates);

  const container = useMemo(() => {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.pointerEvents = "auto";
    div.style.left = `${left}px`;
    div.style.top = `${top}px`;
    div.style.zIndex = String(zIndex);
    return div;
  }, [left, top, zIndex]);

  const handleClickOutside = useCallback(
    (event) => {
      if (onClickOutside && container && !container.contains(event.target as HTMLElement)) {
        // If we have modal or dropdown opened in front of overlay - do not consider those clicks
        // as outside clicks, rather direct clicks for default handling by modal/dropdown itself
        const isGlobalTarget =
          (!usedInModal && !!event.target.closest(".ant-modal-root")) ||
          !!event.target.closest(".ant-popover") ||
          !!event.target.closest(".ant-select-dropdown") ||
          !!event.target.closest(".ant-modal") ||
          !!event.target.closest(".ant-dropdown-menu") ||
          !!event.target.closest(`.${EMAIL_WARNING_MODAL_CLASS_NAME}`);

        if (!isGlobalTarget) {
          event.preventDefault();
          event.stopPropagation();
          onClickOutside();
        }
      }
    },
    [container, onClickOutside, usedInModal]
  );

  useEffect(() => {
    const child = document.body.appendChild(container);
    return () => {
      document.body.removeChild(child);
    };
  }, [map, container]);

  useEffect(() => {
    google.maps.OverlayView.preventMapHitsAndGesturesFrom(container);

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [container, handleClickOutside]);

  const [width, height] = useRawSizeObserver(container);
  useLayoutEffect(() => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    if (left + width >= screenWidth) {
      // try to position it to the left side of the point
      let newLeft = left - width - (coordinates ? 0 : X_GAP);
      if (newLeft < 0) {
        newLeft = 0;
      }
      container.style.left = `${newLeft}px`;
    }
    // TODO: this logic should be checked on a scrolled down window
    // may we also will need to subtract scrollTop before comparing with screenHeight
    if (top + height >= screenHeight) {
      let newTop = top - height - (coordinates ? 0 : Y_GAP);
      if (newTop < 0) {
        newTop = 0;
      }
      container.style.top = `${newTop}px`;
    }
  }, [width, height, container, left, top, coordinates]);

  return createPortal(children, container);
};

export default Overlay;
